import React, { ReactElement } from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { useIsMobileOrTablet, useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { StationeryTemplateCategoryEnum } from '@graphql/generated';
import { ChoiceGroup, QuantityDialogProvider } from '@apps/card/routes/CardCustomizer';
import { StationeryProductSelector } from '../../ProductDetailsPage.types';
import { Breadcrumbs } from '../Breadcrumbs';
import { CustomizeNowButton } from '../../CustomizeNowButton';
import { Layout } from '../Layout/Layout';
import { TitleBlock } from '../TitleBlock';
import { Skeleton } from '@shared/components/Skeleton';
import { useFoilConfig } from '@apps/card/hooks/useFoilConfig';
import { useDisplayPrice } from '../../useDisplayPrice';
import { useSaleColor } from '../../../DesignsGallery/utils/useSaleColor';
import { useProductToUIMapping } from '@apps/card/ProductUIConfig/useProductToUIMapping';
import { useStationeryProductPricingContext } from '@apps/card/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import { useGetDiscountByVendor } from '../../../DesignsGallery/components/GalleryItem/hooks/useGetDiscountByVendor';
import { StationeryProduct } from '@shared/utils/print-price/types';
import { Product } from '@apps/card/ProductUIConfig/product.types';
import { RelatedItems } from '../RelatedItems';
import { useFeatureValue } from '@shared/core/featureFlags';

interface PdpLayoutProps {
  themeId: string;
  designName: string;
  templateCategory: StationeryTemplateCategoryEnum;
  showCustomizeButtonInHeader: boolean;
  titleBlock: () => JSX.Element;
  onCustomizeNowClick: () => void;
  onSecondaryCtaClick?: () => void;
  secondaryCtaLabel?: string;
  isCreatingDraft: boolean;
  // cardFormatControl: CardFormatControl;
  productSelector: StationeryProductSelector;
  subtotal?: string;
  previewPane: () => JSX.Element;
  customizationPane: () => JSX.Element;
  disableCustomizeNowButton?: boolean;
  isPremium?: boolean;
  isFoil?: boolean;
}

const CONTENT_HORIZONTAL_PADDING = { _: 6, sm2: 7 };
const Container: React.FC = ({ children }) => {
  return (
    <Flex justifyContent="center" width="100%">
      <Layout.Container display="flex" flexDirection="column" alignItems="center" height="100%" width={1280} maxWidth="100%">
        {children}
      </Layout.Container>
    </Flex>
  );
};

const Header: React.FC<{ showCustomizeButtonInHeader: boolean }> = ({ children, showCustomizeButtonInHeader }) => {
  return (
    <Box width="100%" marginBottom={{ _: 6, sm2: 0 }} marginTop={{ _: 6, sm2: 0 }} paddingX={CONTENT_HORIZONTAL_PADDING}>
      <Layout.Header alignItems="center" paddingY={{ sm2: 4 }} display="flex" justifyContent="space-between" paddingRight={showCustomizeButtonInHeader ? 60 : undefined}>
        {children}
      </Layout.Header>
    </Box>
  );
};

const Body: React.FC = ({ children }) => {
  return (
    <Box width="100%" paddingX={CONTENT_HORIZONTAL_PADDING} marginBottom={60}>
      <Layout.Body>{children}</Layout.Body>
    </Box>
  );
};

const PreviewPanel = ({ title, content }: { title: () => JSX.Element; content: () => JSX.Element }) => {
  const isMobile = useIsMobileScreen();

  return (
    <Layout.LeftPanel>
      {isMobile && title()}
      {content()}
    </Layout.LeftPanel>
  );
};

const DiscountedPriceText = ({ originalPrice, newPrice }: { originalPrice: string; newPrice: string }) => {
  const saleColor = useSaleColor();

  return (
    <TextV2>
      <TextV2 as="span" typographyVariant="body3">
        From
      </TextV2>{' '}
      <TextV2 as="span" textDecoration="line-through" color="mono10" typographyVariant="body3">
        {originalPrice}
      </TextV2>{' '}
      <TextV2 as="span" color={saleColor} fontWeight={600 as const} typographyVariant="body3">
        {newPrice}
      </TextV2>
    </TextV2>
  );
};

const ActionItem = ({ selected, title, price }: { selected: boolean; title: string; price?: string | ReactElement }) => {
  return (
    <Flex justifyContent="space-between" alignItems="center" flex={1} paddingX={3}>
      <TextV2 typographyVariant="body1" color="mono12" textTransform="capitalize" fontWeight={selected ? 600 : 400}>
        {title}
      </TextV2>
      {typeof price === 'string' ? (
        <TextV2 typographyVariant="body3" color="mono12" fontWeight={selected ? 600 : 400}>
          {price}
        </TextV2>
      ) : (
        price
      )}
    </Flex>
  );
};

const ActionsPanel = ({
  productSelector,
  content,
  title,
  isPremium,
  isFoil
}: {
  content: () => JSX.Element;
  title: () => JSX.Element;
  productSelector?: PdpLayoutProps['productSelector'];
  isPremium?: boolean;
  isFoil?: boolean;
}) => {
  const isMobile = useIsMobileScreen();

  // const { value: printEnableDigitalEditor } = useFeatureValue('printEnableDigitalEditor');

  const { pricePerCardInMinorUnits: foilPrice } = useFoilConfig();
  const displayPrice = useDisplayPrice({ taylor: isFoil ? foilPrice : 0 });

  return (
    <Layout.RightPanel>
      <Flex flexDirection="column" paddingRight={{ md: 8 }} marginTop={{ _: 0, sm2: 0 }}>
        {!isMobile && title()}
        {productSelector && <ProductSelector productSelector={productSelector} pricing={displayPrice} />}
        {content()}
      </Flex>
    </Layout.RightPanel>
  );
};

const isProduct = (input: unknown): input is StationeryProduct & Product => {
  return !!input;
};

const ProductSelector = (props: { productSelector: PdpLayoutProps['productSelector']; pricing: ReturnType<typeof useDisplayPrice> }) => {
  const { productSelector } = props;
  const { getProductWithUIMapping } = useProductToUIMapping();
  const { products: productInfo, activeProduct, priceCalculator } = useStationeryProductPricingContext();
  const products = productInfo.map(p => getProductWithUIMapping(p)).filter(isProduct);
  const minorUnitsToPriceString = useMinorUnitsToPriceString();
  const { getDiscountByVendor } = useGetDiscountByVendor();

  if (products.length === 0) {
    return null;
  }

  return (
    <Box marginBottom={7}>
      <ChoiceGroup
        title="Product"
        getName={() => (products.length === 1 ? activeProduct.translations.formatName : '')}
        value={productSelector.productId}
        minHeight={55}
        choices={products.map(p => p.id)}
        setValue={productSelector.onChange}
        columns={1}
      >
        {products.map((product, index) => {
          const discount = getDiscountByVendor(product.vendor);
          const priceData = priceCalculator.getProductBasePrice(product.id, product.defaultQuantity, discount, true);
          const priceDataDiscounts = priceData.discounts;
          const unitPriceString = minorUnitsToPriceString(priceData.unitPriceInMinorUnits, { canShowFreeLabel: true });
          const preDiscountUnitPriceString = priceDataDiscounts && minorUnitsToPriceString(priceDataDiscounts.preDiscountedUnitPriceInMinorUnits, { canShowFreeLabel: true });

          const isDiscountDifferent = preDiscountUnitPriceString !== unitPriceString;
          const canDisplayDiscount = discount && preDiscountUnitPriceString && isDiscountDifferent;
          let priceDisplay = canDisplayDiscount ? <DiscountedPriceText originalPrice={preDiscountUnitPriceString} newPrice={unitPriceString} /> : `From ${unitPriceString}`;
          if (priceDisplay === 'From FREE') {
            priceDisplay = 'FREE';
          }

          return <ActionItem key={index} selected={product.id === activeProduct.id} title={product.translations.displayName} price={priceDisplay} />;
        })}
      </ChoiceGroup>
    </Box>
  );
};
const Footer: React.FC = ({ children }) => {
  return (
    <Box
      justifyContent="center"
      borderTop="1px solid"
      borderTopColor="mono3"
      paddingX={CONTENT_HORIZONTAL_PADDING}
      paddingY={6}
      position="sticky"
      bottom={0}
      width="100%"
      backgroundColor="white"
      // 10 - to be over the card preview (it has zIndex=5)
      zIndex={10}
    >
      {children}
    </Box>
  );
};

export const PdpLayout = (props: PdpLayoutProps) => {
  const {
    themeId,
    disableCustomizeNowButton,
    isCreatingDraft,
    previewPane,
    customizationPane,
    designName,
    templateCategory,
    titleBlock,
    onCustomizeNowClick,
    showCustomizeButtonInHeader,
    productSelector,
    isPremium,
    isFoil = false
  } = props;

  const printPdpRelatedProductsEnabled = useFeatureValue('printPdpEnableRelatedProducts').value === 'on';

  const isMobileOrTablet = useIsMobileOrTablet();

  return (
    <QuantityDialogProvider isInsidePDP>
      <Container>
        <Header showCustomizeButtonInHeader={showCustomizeButtonInHeader}>
          <Breadcrumbs designName={designName} templateCategory={templateCategory} />
          {showCustomizeButtonInHeader && <CustomizeNowButton disabled={disableCustomizeNowButton} onCustomizeClick={onCustomizeNowClick} isLoading={isCreatingDraft} />}
        </Header>
        <Body>
          <PreviewPanel title={titleBlock} content={previewPane} />
          <ActionsPanel productSelector={productSelector} title={titleBlock} content={customizationPane} isPremium={isPremium} isFoil={isFoil} />
        </Body>
        {printPdpRelatedProductsEnabled && <RelatedItems category={templateCategory} themeId={themeId} />}
        {isMobileOrTablet && (
          <Footer>
            <CustomizeNowButton disabled={disableCustomizeNowButton} fullWidth onCustomizeClick={onCustomizeNowClick} isLoading={isCreatingDraft} />
          </Footer>
        )}
      </Container>
    </QuantityDialogProvider>
  );
};

const PdpLayoutSkeleton = () => {
  return (
    <Container>
      <Header showCustomizeButtonInHeader={false}>
        <Breadcrumbs.Skeleton />
      </Header>
      <Body>
        <PreviewPanel title={TitleBlock.Skeleton} content={() => <Skeleton aspectRatio={'1'} />} />
        <ActionsPanel
          productSelector={undefined}
          title={TitleBlock.Skeleton}
          content={() => (
            <Flex flexDirection="column" rowGap={7}>
              <ChoiceGroup.Skeleton minHeight={55} choices={2} />
              <ChoiceGroup.Skeleton choices={0} disablePriceSkeleton />
              <ChoiceGroup.Skeleton choices={4} />
              <ChoiceGroup.Skeleton choices={3} />
              <ChoiceGroup.Skeleton choices={1} columns={1} minHeight={50} />
              <ChoiceGroup.Skeleton choices={1} columns={1} minHeight={48} disablePriceSkeleton />
            </Flex>
          )}
        />
      </Body>
    </Container>
  );
};

PdpLayout.Skeleton = PdpLayoutSkeleton;
